import React from 'react';
import { motion } from 'framer-motion';
import Seo from '../../components/Seo';
import { seo } from '../../seo/seoData';
import { useTranslation } from 'react-i18next';
import { FileText, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import LegalDraftBanner from '../../components/LegalDraftBanner';

// =============================================================================
// LEGAL DRAFT — REQUIRES AVOCAT REVIEW BEFORE B2C PUBLIC LAUNCH
// =============================================================================
// The CGU content was scaffolded by an earlier session. It MUST be reviewed
// and finalized by an avocat (specialised in droit du numérique + transport)
// before public launch. Known gaps to close:
//   - Cooling-off period (droit de rétractation) for B2C consumers — Code de
//     la consommation requires this
//   - Article on data subprocessors handling: Stripe, Twilio, etc.
//   - Cancellation / refund terms specifics (rather than a separate page)
//   - Pricing rules per VTC regulation (Article L. 3122 Code des transports)
//   - Cross-reference with corresponding chauffeur contract template
//   - Verification of "tribunaux de Nantes" jurisdiction clause for B2C — for
//     consumers, Article R.121-4 Code de la consommation may require allowing
//     consumer's residence court too
//
// FR is authoritative; non-FR versions are courtesy translations marked via
// LegalDraftBanner.
// =============================================================================

const CGUPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-black pt-20" data-testid="cgu-page">
          <Seo {...seo.cgu} />
      <LegalDraftBanner />
      <section className="py-24 md:py-32">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Back link */}
            <Link to="/" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors mb-8">
              <ArrowLeft className="w-4 h-4" />
              {t('legalPages.common.backToHome')}
            </Link>

            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
                  {t('legalPages.cgu.title')}
                </h1>
                <p className="text-neutral-500 text-sm mt-1">{t('legalPages.common.lastUpdated')}</p>
              </div>
            </div>

            <div className="prose prose-invert max-w-none space-y-10">
              {/* Article 1 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.cgu.art1.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.cgu.art1.p1')}</p>
                <p className="text-neutral-400 leading-relaxed mt-4">{t('legalPages.cgu.art1.p2')}</p>
              </section>

              {/* Article 2 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.cgu.art2.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.cgu.art2.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4].map(i => <li key={i}>{t(`legalPages.cgu.art2.list${i}`)}</li>)}
                </ul>
                <p className="text-neutral-400 leading-relaxed mt-4">{t('legalPages.cgu.art2.outro')}</p>
              </section>

              {/* Article 3 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.cgu.art3.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.cgu.art3.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4, 5, 6].map(i => <li key={i}>{t(`legalPages.cgu.art3.list${i}`)}</li>)}
                </ul>
              </section>

              {/* Article 4 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.cgu.art4.title')}</h2>
                {[1, 2, 3].map(i => (
                  <React.Fragment key={i}>
                    <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t(`legalPages.cgu.art4.sub${i}Title`)}</h3>
                    <p className="text-neutral-400 leading-relaxed">{t(`legalPages.cgu.art4.sub${i}Body`)}</p>
                  </React.Fragment>
                ))}
              </section>

              {/* Article 5 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.cgu.art5.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.cgu.art5.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4, 5, 6, 7].map(i => <li key={i}>{t(`legalPages.cgu.art5.list${i}`)}</li>)}
                </ul>
              </section>

              {/* Article 6 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.cgu.art6.title')}</h2>
                {[1, 2, 3].map(i => (
                  <React.Fragment key={i}>
                    <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t(`legalPages.cgu.art6.sub${i}Title`)}</h3>
                    <p className="text-neutral-400 leading-relaxed">{t(`legalPages.cgu.art6.sub${i}Body`)}</p>
                  </React.Fragment>
                ))}
              </section>

              {/* Article 7 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.cgu.art7.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.cgu.art7.p1')}</p>
                <p className="text-neutral-400 leading-relaxed mt-4">{t('legalPages.cgu.art7.p2')}</p>
              </section>

              {/* Article 8 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.cgu.art8.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">
                  {t('legalPages.cgu.art8.body')}{' '}
                  <Link to="/legal/mentions-legales" className="text-white underline hover:no-underline">
                    {t('legalPages.cgu.art8.linkLabel')}
                  </Link>.
                </p>
              </section>

              {/* Article 9 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.cgu.art9.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.cgu.art9.body')}</p>
              </section>

              {/* Article 10 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.cgu.art10.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.cgu.art10.body')}</p>
              </section>

              {/* Article 11 - Contact */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.cgu.art11.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.cgu.art11.intro')}</p>
                <div className="mt-4 text-neutral-400">
                  <p>Black Matte</p>
                  <p>Nantes, France</p>
                  <p>Téléphone : 09 39 24 23 20</p>
                  <p>Email : contact@blackmatte.fr</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CGUPage;
